import React from 'react';
import { Alert, Container, Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getFileById } from '../apis/fileApi';
import { dateTimeFormatter, fileSizeFormatter } from '../library/formatters';
import { FileJobsTable } from '../components/Files/FileJobsTable';

const displayValue = (value) => {
	if (value === null || value === undefined || value === '') {
		return 'Not available';
	}
	return value;
};

const getTimestamp = (value) => {
	if (value === null || value === undefined) {
		return 'Not available';
	}
	const timestamp = typeof value === 'object' ? value.timestamp : value;
	return timestamp ? dateTimeFormatter({ value: timestamp }) : 'Not available';
};

const getUserName = (value) => {
	const user = value?.user ?? value;
	if (typeof user === 'string') {
		return displayValue(user);
	}
	return displayValue(user?.name ?? user?.displayName);
};

const getCollectionSummary = (summary, label) => {
	if (summary === null || summary === undefined) {
		return 'None';
	}

	const count = summary.count ?? summary.totalCount ?? summary.total ??
		(Array.isArray(summary.items) ? summary.items.length : undefined) ??
		(Array.isArray(summary.ids) ? summary.ids.length : undefined);

	if (count !== undefined) {
		return `${count} ${label}${count === 1 ? '' : 's'}`;
	}
	if (Array.isArray(summary)) {
		return `${summary.length} ${label}${summary.length === 1 ? '' : 's'}`;
	}
	if (typeof summary === 'object') {
		return JSON.stringify(summary);
	}
	return summary;
};

const DetailRow = ({ label, children }) => (
	<tr>
		<th scope="row">{label}</th>
		<td>{children}</td>
	</tr>
);

export const FilePage = () => {
	const { id } = useParams();
	const { data: file, isLoading, isError, error } = useQuery({
		queryKey: ['file', id],
		queryFn: async () => {
			const file = await getFileById(id);
			return file;
		},
		enabled: Boolean(id),
	});

	if (isLoading) {
		return (
            <Container fluid className="loading-container py-4" aria-live="polite">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
                        <span className="visually-hidden">Loading file details...</span>
                    </div>
                    <div className="mt-3">
                        <h5>Loading File {id}</h5>
                        <p className="text-muted">Please wait while the file details are loaded...</p>
                    </div>
                </div>
            </Container>
		);
	}

	if (isError) {
		const message = error?.status === 403
			? 'You do not have permission to view this file.'
			: 'An error occurred while fetching the file details.';
		return (
			<Container fluid className="py-4">
				<Alert variant="danger" role="alert">{message}</Alert>
			</Container>
		);
	}

	if (!file) {
		return (
			<Container fluid className="py-4">
				<Alert variant="warning" role="alert">File not found.</Alert>
				<Link to="/jobs">Back to jobs</Link>
			</Container>
		);
	}

	const jobCount = file.jobs?.total ?? file.jobs?.count ?? file.jobs?.totalCount ??
		file.jobs?.items?.length ?? file.jobs?.ids?.length ??
		(Array.isArray(file.jobs) ? file.jobs.length : 0);

	return (
		<Container fluid className="py-4">
			<h3 className="display-6 mb-4">{displayValue(file.fileName)}</h3>

			<section aria-labelledby="file-information-heading" className="mb-4">
				<h4 id="file-information-heading">File information</h4>
				<Table id="file-information-table" size="sm" bordered>
					<tbody>
						<DetailRow label="File ID">{displayValue(file.id ?? id)}</DetailRow>
						<DetailRow label="Type">{displayValue(file.type)}</DetailRow>
						<DetailRow label="MIME type">{displayValue(file.mimeType)}</DetailRow>
						<DetailRow label="File size">
							{file.fileSize === null || file.fileSize === undefined
								? 'Not available'
								: fileSizeFormatter({ value: file.fileSize })}
						</DetailRow>
						<DetailRow label="Status">{displayValue(file.status)}</DetailRow>
						<DetailRow label="Status message">{displayValue(file.statusMessage)}</DetailRow>
						<DetailRow label="Created">{getTimestamp(file.created)}</DetailRow>
						<DetailRow label="Created by">{getUserName(file.created)}</DetailRow>
						<DetailRow label="Updated">{getTimestamp(file.updated)}</DetailRow>
						<DetailRow label="Updated by">{getUserName(file.updated)}</DetailRow>
						<DetailRow label="Jobs">{getCollectionSummary(file.jobs, 'job')}</DetailRow>
						<DetailRow label="Job tasks">{getCollectionSummary(file.jobTasks, 'job task')}</DetailRow>
					</tbody>
				</Table>
			</section>

			<section aria-labelledby="file-metadata-heading">
				<h4 id="file-metadata-heading">Metadata</h4>
				{file.metadata && Object.keys(file.metadata).length > 0 ? (
					<Table id="file-metadata-table" size="sm" bordered>
						<thead>
							<tr><th scope="col">Key</th><th scope="col">Value</th></tr>
						</thead>
						<tbody>
							{Object.entries(file.metadata).map(([key, value]) => (
								<tr key={key}><th scope="row">{key}</th><td>{displayValue(value)}</td></tr>
							))}
						</tbody>
					</Table>
				) : (
					<p className="text-muted">No metadata available.</p>
				)}
			</section>

            <section aria-labelledby="file-jobs">
			{jobCount > 0? 
                <FileJobsTable fileId = {id}/>
                :<><Container fluid className="py-4" aria-live="polite">
				<p className="text-muted">No jobs associataed with id.</p>
			</Container></>
            }
            </section>
		</Container>
	);
};
